/* eslint-disable prettier/prettier */
import React, { createContext, useCallback, useState, useContext } from 'react';

import api from '../services/api';
import { formatPrice } from '../utils/funcoes';
import getResponseErrors from '../utils/getResponseErrors';
import { useToast } from './toast';

interface INotafiscal {
  CprCodigo: number;
  CprDataPag: string;
  FormaPagto: string;
  NfeCodigoVerificacao?: string;
  NfeCprCodigo?: number;
  NfeLinkNFSe?: string;
  NfeNumero: string;
  NfeSituacao: string;
  NfeDataEmissao?: string;
  NumeroNF?: string;
  NfeNumeroNFSe?: string;
  descricao: string;
  nome: string;
  vlr: number;
  vlrFormatado: string;
  linkNota?: string;
  NfeId?: string;
}

interface NotaFiscalContextData {
  loading: boolean;
  notasFiscais: INotafiscal[];
  buscaNotas(requisicao: string): Promise<void>;
  setNotaFiscal(dadosNota: INotafiscal): void;
}

const NotaFiscalContext = createContext<NotaFiscalContextData>(
  {} as NotaFiscalContextData,
);

const NotaFiscalProvider: React.FC = ({ children }) => {
  const [loading, setLoading] = useState(false);

  const { addToast } = useToast();
  const [notasFiscais, setNotasFiscais] = useState<INotafiscal[]>([]);

  const buscaNotas = useCallback(
    async requisicao => {
      setLoading(true);
      try {
        setNotasFiscais([]);
        const response = await api.get<INotafiscal[]>(requisicao);

        if (response.data.length === 0) {
          addToast({ type: 'info', title: 'Nenhum registro encontrado' });
          setNotasFiscais([]);
          return;
        }
        const notas = response.data.map(item => {
          return {
            ...item,
            vlrFormatado: formatPrice(item.vlr),
            linkNota:
              Number(item.NumeroNF || '5032') < 5031 &&
                item.NfeLinkNFSe &&
                item.NfeLinkNFSe.includes('google')
                ? item.NfeLinkNFSe
                : item.linkNota,
          };
        });
        setNotasFiscais(notas);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar notas',
          type: 'error',
          description: mensagemErro,
        });
        setNotasFiscais([]);
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const setNotaFiscal = useCallback(
    (dadosNota: INotafiscal) => {
      const index = notasFiscais.findIndex(
        nf =>
          nf.CprCodigo === dadosNota.NfeCprCodigo &&
          (nf.NfeId === dadosNota.NfeId || !nf.NfeId || nf.NfeId === ''),
      );

      const arrayNotas = notasFiscais.map(nf => nf);
      arrayNotas[index] = {
        ...notasFiscais[index],
        NfeSituacao: dadosNota.NfeSituacao
          ? dadosNota.NfeSituacao
          : 'Processando',
        NfeLinkNFSe: dadosNota.NfeLinkNFSe,
        NumeroNF: dadosNota.NfeNumeroNFSe,
        NfeNumero: dadosNota.NfeNumero,
        NfeCprCodigo: dadosNota.NfeCprCodigo,
        NfeCodigoVerificacao: dadosNota.NfeCodigoVerificacao,
        linkNota: dadosNota.linkNota,
      };
      setNotasFiscais(arrayNotas);
    },
    [notasFiscais],
  );

  return (
    <NotaFiscalContext.Provider
      value={{ loading, notasFiscais, buscaNotas, setNotaFiscal }}
    >
      {children}
    </NotaFiscalContext.Provider>
  );
};

function useNotaFiscal(): NotaFiscalContextData {
  const context = useContext(NotaFiscalContext);

  if (!context) {
    throw new Error('useNotaFiscal deve ser usado com o NotaFiscalProvider');
  }

  return context;
}

export { NotaFiscalProvider, useNotaFiscal };
